"use client";

import { useEffect } from "react";

import { ReferralSchedulingRecommendation } from "@/src/components/comms/referral-scheduling-recommendation";
import { CopyableContent } from "@/src/components/copyable-content";
import { EhrInstructionsCard } from "@/src/components/ehr-instructions-card";
import ErrorAlert from "@/src/components/error-alert";
import { AthenaReferralCards } from "@/src/components/tasks/athena-referral-cards";
import { StediEligibilitySummary } from "@/src/components/tasks/stedi-eligibility-summary";
import { SyncIncompleteActions } from "@/src/components/tasks/sync-incomplete-actions";
import { useProvidersForDiagnostics } from "@/src/lib/data-fetchers/diagnosis-providers-fetchers";
import { useLocationAndProvidersInstructions } from "@/src/lib/data-fetchers/location-providers-fetchers";
import { useStediEligibilityCheck } from "@/src/lib/data-fetchers/stedi-eligibility-check-fetchers";
import { logger } from "@/src/lib/logging/logger";
import { tryGetEhrSystemId } from "@/src/lib/resolve-ehr-ids";
import { getFormattedAddress } from "@/src/models/address";
import { Appointment } from "@/src/models/appointment";
import { Coverage } from "@/src/models/coverage";
import { Referral } from "@/src/models/documents/referral";
import { EHRDocument } from "@/src/models/ehr-document";
import { Patient } from "@/src/models/patient";
import { getPersonName } from "@/src/models/person";
import { getPractitionerNameWithCredentials, Practitioner } from "@/src/models/practitioner";

// Define the Athena data interface using our typed models
interface AthenaData {
  patientDetails: Patient | null;
  coverages: Coverage[];
  careTeam: Practitioner[];
  appointmentReminders: Appointment[];
  bookedAppointments: Appointment[];
  document: EHRDocument | null;
  expectedDocument: EHRDocument | null;
  documentPatientId?: string | null;
}

interface AthenaReferralInstructionsProps {
  referral: Referral;
  departmentId?: string;
  athenaData: AthenaData | null;
  customInstructions: string | null;
  showSchedulingRecommendation?: boolean;
  taskId: string;
}

export function AthenaReferralInstructions({
  referral,
  departmentId,
  athenaData,
  customInstructions,
  showSchedulingRecommendation = false,
  taskId,
}: AthenaReferralInstructionsProps) {
  useEffect(() => {
    logger.info("Data from Athena", { athenaData });
  }, [athenaData]);

  const { eligibilityCheck } = useStediEligibilityCheck(referral.id);

  // Get location and providers instructions based on referral's receiving location
  const { data: locationProvidersData } = useLocationAndProvidersInstructions(referral.receivingLocation?.id || "");

  const { data: providersForDiagnostics } = useProvidersForDiagnostics(referral.diagnosisList || [], departmentId);

  const locationProviders = locationProvidersData?.providers ?? [];
  const hasReceivingProviders = locationProviders.length > 0 || (providersForDiagnostics?.length ?? 0) > 0;

  const receivingLocationAddress = referral.receivingLocation?.address
    ? getFormattedAddress(referral.receivingLocation.address) || "N/A"
    : "N/A";

  const patientName = getPersonName(athenaData?.patientDetails, true, true) || "N/A";
  const ehrId = (athenaData?.patientDetails && tryGetEhrSystemId(athenaData.patientDetails)) || "N/A";
  const documentId = athenaData?.document?.ehrId ?? "N/A";
  const referringProviderName = referral.referringProvider
    ? getPractitionerNameWithCredentials(referral.referringProvider)
    : "N/A";
  const referringProviderNPI = referral.referringProvider?.npiNumber || "N/A";
  const referringLocationAddress = referral.referringLocation?.address
    ? getFormattedAddress(referral.referringLocation.address) || "N/A"
    : "N/A";

  return (
    <div className="space-y-8 text-foreground">
      {!athenaData && <ErrorAlert compact={true} message="No Athena data available" />}

      <EhrInstructionsCard>
        <div className="text-sm text-muted-foreground">
          <h3 className="text-md font-semibold text-center mb-4">Athena Referral Instructions</h3>

          {/* Patient + Referring Provider: flat 6-field grid */}
          <div className="grid grid-cols-3 gap-y-4 mb-6">
            <div>
              <span className="font-medium text-foreground">Patient Name: </span>
              <CopyableContent text={patientName} />
            </div>
            <div>
              <span className="font-medium text-foreground">EHR ID: </span>
              <CopyableContent text={ehrId} />
            </div>
            <div>
              <span className="font-medium text-foreground">Document ID: </span>
              <CopyableContent text={documentId} />
            </div>
            {athenaData?.documentPatientId && (
              <div>
                <span className="font-medium text-foreground">Document Patient ID: </span>
                <CopyableContent text={athenaData.documentPatientId} />
              </div>
            )}
            <div className={athenaData?.documentPatientId ? "" : "col-start-2"}>
              <span className="font-medium text-foreground">Referring Provider: </span>
              <CopyableContent text={referringProviderName} />
            </div>
            <div>
              <span className="font-medium text-foreground">NPI: </span>
              <CopyableContent text={referringProviderNPI} />
            </div>
            <div className="col-span-3">
              <span className="font-medium text-foreground">Referring Location: </span>
              <CopyableContent text={referringLocationAddress} />
            </div>
          </div>

          {/* Receiving Location and Providers Table - only show if we have data */}
          {hasReceivingProviders && (
            <div className="mb-4 my-2">
              <h3 className="font-semibold text-foreground my-2">Receiving Location and Providers:</h3>
              <div className="overflow-x-auto">
                <table className="w-full border border-border rounded-lg">
                  <thead className="bg-muted">
                    <tr>
                      <th className="text-left p-3 font-medium text-foreground">Receiving Location</th>
                      <th className="text-left p-3 font-medium text-foreground">Department</th>
                      <th className="text-left p-3 font-medium text-foreground">Provider Group</th>
                      <th className="text-left p-3 font-medium text-foreground">Providers</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t border-border">
                      <td className="p-3 text-sm">
                        <span className="text-muted-foreground">
                          <CopyableContent text={receivingLocationAddress} />
                        </span>
                      </td>
                      <td className="p-3 text-sm">
                        <span className="text-muted-foreground">
                          <CopyableContent text={locationProvidersData?.department?.name || "N/A"} />
                        </span>
                      </td>
                      <td className="p-3 text-sm">
                        <span className="text-muted-foreground">
                          <CopyableContent text={locationProvidersData?.department?.providerGroup || "N/A"} />
                        </span>
                      </td>
                      <td className="p-3 text-sm">
                        <div className="space-y-1">
                          {locationProviders.length > 0
                            ? locationProviders.map((provider) => (
                                <div key={provider}>
                                  <span className="text-muted-foreground">
                                    <CopyableContent text={provider} />
                                  </span>
                                </div>
                              ))
                            : (providersForDiagnostics ?? []).map((provider) => (
                                <div key={provider.name}>
                                  <span
                                    className={
                                      provider.isPreferred
                                        ? "font-semibold text-muted-foreground"
                                        : "text-muted-foreground"
                                    }
                                  >
                                    <CopyableContent text={provider.name} />
                                  </span>
                                </div>
                              ))}
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {showSchedulingRecommendation && <ReferralSchedulingRecommendation referral={referral} />}

          {customInstructions && <p className="text-sm mt-4">{customInstructions}</p>}

          {eligibilityCheck && <StediEligibilitySummary check={eligibilityCheck} />}

          <div className="mt-4">
            <SyncIncompleteActions taskId={taskId} />
          </div>
        </div>
      </EhrInstructionsCard>

      <AthenaReferralCards referral={referral} athenaData={athenaData} />
    </div>
  );
}
